/**
 * Shared skeleton loaders (PD-14692). Primary content surfaces load
 * skeleton-first — shaped like the content that replaces them — instead of
 * a bare spinner, so the page doesn't jump when data lands. Every block
 * uses the mode-aware `bg-brand-input` token (PD-14673 precedent) so both
 * themes read correctly.
 *
 * All skeletons are `aria-hidden` — screen readers get the page's own
 * loading semantics (route announcements / final content), not a tree of
 * meaningless pulsing boxes.
 */

/**
 * History-list skeleton: 3-4 cards matching the history card dimensions
 * (title + meta lines on the left, amount on the right) used by the
 * invoices, orders, statements, and transactions list pages.
 */
export function HistoryListSkeleton({
	cards = 4,
}: {
	cards?: number;
}): JSX.Element {
	return (
		<div className="space-y-2" aria-hidden="true">
			{Array.from({ length: cards }, (_, i) => (
				<div
					key={i}
					className="bg-brand-surface border border-brand-border rounded-xl p-4 animate-pulse"
				>
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0 flex-1">
							<div className="h-4 bg-brand-input rounded w-2/5 mb-2" />
							<div className="h-3 bg-brand-input rounded w-3/5 mb-1.5" />
							<div className="h-3 bg-brand-input rounded w-2/5" />
						</div>
						<div className="text-right shrink-0">
							<div className="h-4 bg-brand-input rounded w-16 mb-1.5 ml-auto" />
							<div className="h-3 bg-brand-input rounded w-10 ml-auto" />
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

/**
 * Detail-page skeleton: a header card plus a handful of line rows —
 * matches the invoice / order / statement detail layout closely enough
 * that the swap to real content doesn't reflow the page.
 */
export function DetailPageSkeleton(): JSX.Element {
	return (
		<div className="space-y-4 animate-pulse" aria-hidden="true">
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4">
				<div className="h-5 bg-brand-input rounded w-1/3 mb-3" />
				<div className="h-3 bg-brand-input rounded w-1/2 mb-2" />
				<div className="h-3 bg-brand-input rounded w-2/5" />
			</div>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 space-y-3">
				{Array.from({ length: 4 }, (_, i) => (
					<div key={i} className="flex items-center justify-between gap-3">
						<div className="h-3 bg-brand-input rounded w-1/2" />
						<div className="h-3 bg-brand-input rounded w-14" />
					</div>
				))}
			</div>
		</div>
	);
}

/**
 * Account-snapshot skeleton: mirrors the widget's 2-column metric grid
 * (label over value) so the balance / credit numbers land without a jump.
 */
export function SnapshotMetricsSkeleton(): JSX.Element {
	return (
		<div
			className="flex-1 grid grid-cols-2 gap-4 animate-pulse"
			aria-hidden="true"
		>
			{[0, 1].map((i) => (
				<div key={i}>
					<div className="h-3 bg-brand-input rounded w-20 mb-2" />
					<div className="h-6 bg-brand-input rounded w-24" />
				</div>
			))}
		</div>
	);
}

/**
 * Recent-list widget skeleton: compact rows matching the Recent
 * Invoices / Orders / Transactions / Statements dashboard cards (p-3
 * rows, two lines left + amount right). The caller renders its real
 * header above so only the data area pulses.
 */
export function RecentListRowsSkeleton({
	rows = 3,
}: {
	rows?: number;
}): JSX.Element {
	return (
		<div className="flex flex-col gap-2 animate-pulse" aria-hidden="true">
			{Array.from({ length: rows }, (_, i) => (
				<div
					key={i}
					className="bg-brand-surface border border-brand-border rounded-xl p-3"
				>
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0 flex-1">
							<div className="h-3.5 bg-brand-input rounded w-1/2 mb-2" />
							<div className="h-3 bg-brand-input rounded w-2/3" />
						</div>
						<div className="h-3.5 bg-brand-input rounded w-12 shrink-0" />
					</div>
				</div>
			))}
		</div>
	);
}

/**
 * Item-carousel skeleton: a horizontal strip of item-card blocks matching
 * the ItemCarousel card dimensions (square image + two title lines), for
 * the popular / reorder / by-category / promo-items / category widgets.
 */
export function ItemCarouselSkeleton({
	cards = 4,
}: {
	cards?: number;
}): JSX.Element {
	return (
		<div
			className="flex gap-3 overflow-hidden pb-2 -mx-1 px-1 animate-pulse"
			aria-hidden="true"
		>
			{Array.from({ length: cards }, (_, i) => (
				<div key={i} className="shrink-0 w-[150px] sm:w-[170px] md:w-[180px]">
					<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
						<div className="aspect-square bg-brand-input" />
						<div className="p-2 sm:p-3">
							<div className="h-3 bg-brand-input rounded mb-2 w-3/4" />
							<div className="h-3 bg-brand-input rounded w-1/2" />
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

/**
 * Last-order widget skeleton: summary lines plus the 4-tile thumbnail
 * strip the widget renders for its line previews.
 */
export function LastOrderSkeleton(): JSX.Element {
	return (
		<div className="animate-pulse" aria-hidden="true">
			<div className="h-3.5 bg-brand-input rounded w-1/2 mb-2" />
			<div className="h-3 bg-brand-input rounded w-1/3 mb-4" />
			<div className="grid grid-cols-4 gap-2">
				{Array.from({ length: 4 }, (_, i) => (
					<div key={i} className="aspect-square bg-brand-input rounded-lg" />
				))}
			</div>
		</div>
	);
}
